import { cva } from "class-variance-authority";

import { cn } from "@tudigo-monorepo/core-tudigo-theme";
import { Chip } from "@tudigo-monorepo/web-tudigo-components";

type AvatarType = {
  id: string;
  url: string;
  alt?: string;
};

type AvatarSize = "sm" | "md" | "lg" | "xl" | "2xl" | "3xl" | "4xl" | "5xl";

type AvatarListProps = {
  avatars: AvatarType[];
  maxDisplayed?: number;
  avatarSize?: AvatarSize;
  className?: string;
  shouldDisplayMore?: boolean;
};

const sizeClasses = cva(
  [
    "size-6",
    "size-8",
    "size-10",
    "size-12",
    "size-14",
    "size-16",
    "size-18",
    "size-20",
  ],
  {
    variants: {
      size: {
        sm: "size-6",
        md: "size-8",
        lg: "size-10",
        xl: "size-12",
        "2xl": "size-14",
        "3xl": "size-16",
        "4xl": "size-18",
        "5xl": "size-20",
      },
    },
    defaultVariants: {
      size: "md",
    },
  },
);

const chipHeightClasses = cva(
  ["h-4", "h-6", "h-8", "h-10", "h-12", "h-14", "h-16", "h-18"],
  {
    variants: {
      size: {
        sm: "h-4",
        md: "h-6",
        lg: "h-8",
        xl: "h-10",
        "2xl": "h-12",
        "3xl": "h-14",
        "4xl": "h-16",
        "5xl": "h-18",
      },
    },
    defaultVariants: {
      size: "md",
    },
  },
);

export const AvatarList = ({
  avatars,
  maxDisplayed = 3,
  avatarSize = "md",
  className = "",
  shouldDisplayMore = true,
}: AvatarListProps) => {
  if (!avatars?.length) {
    return null;
  }

  return (
    <div className={cn("flex items-center -space-x-2", className)}>
      {avatars.slice(0, maxDisplayed).map((avatar) => (
        <img
          key={avatar.id}
          className={cn(
            "inline-block rounded-full object-cover ring-2 ring-white",
            sizeClasses({ size: avatarSize }),
          )}
          src={avatar.url}
          alt={avatar.alt || "Avatar"}
        />
      ))}
      {shouldDisplayMore && avatars.length > maxDisplayed && (
        <Chip
          label={`+${avatars.length - maxDisplayed}`}
          type="info"
          className={cn(chipHeightClasses({ size: avatarSize }))}
        />
      )}
    </div>
  );
};
